import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { 
  Users,
  Send,
  Clock,
  CheckCircle,
  MessageSquare,
  Shield,
  Brain,
  Eye,
  ThumbsUp,
  Copy,
  Link as LinkIcon,
  ExternalLink,
  Check,
  Sparkles,
  TrendingUp,
  Lightbulb,
  BarChart3,
  Star,
  MessageSquarePlus
} from 'lucide-react'
import { motion } from 'framer-motion'
import { useSearchParams } from 'react-router-dom'
import type { FeedbackRequest, FeedbackStakeholder } from '@/services/api/feedbackService'
import FeedbackResponseDialog from './FeedbackResponseDialog'
import FeedbackDetailView from './FeedbackDetailView'
import FeedbackRequestDialog from './FeedbackRequestDialog'
import GiveFeedbackDialog from './GiveFeedbackDialog'
import { useAuth } from '@/providers/AuthProvider'
import { feedbackDueDateLabel, isPastRecommendedDate } from './feedbackDueDate'
import { useState, useEffect, useRef } from 'react'
import { integrationService } from '@/services/api/integrationService'
import { feedbackService } from '@/services/api/feedbackService'
import { useJourneyViewMode } from '@/lib/journeyViewMode'
import toast from 'react-hot-toast'

interface StakeholderFeedbackSystemProps {
  userRole: 'employee' | 'manager'
  requests: FeedbackRequest[]
  pendingStakeholderResponses?: FeedbackStakeholder[]
  completedStakeholderResponses?: FeedbackStakeholder[]
  userId: string
  loading?: boolean
  onCreateRequest: (request: any) => void | Promise<any>
  onRefresh?: () => void
}

export default function StakeholderFeedbackSystem({
  userRole,
  requests,
  pendingStakeholderResponses = [],
  completedStakeholderResponses = [],
  userId,
  loading,
  onCreateRequest,
  onRefresh
}: StakeholderFeedbackSystemProps) {
  const [searchParams, setSearchParams] = useSearchParams()
  const { user } = useAuth()
  const [viewMode] = useJourneyViewMode()
  const isDetailed = viewMode === 'detailed'
  const [activeTab, setActiveTab] = useState(userRole === 'employee' ? 'employee-requested' : 'manager-initiated')
  const [responseDialogOpen, setResponseDialogOpen] = useState(false)
  const [detailViewOpen, setDetailViewOpen] = useState(false)
  const [requestDialogOpen, setRequestDialogOpen] = useState(false)
  const [giveDialogOpen, setGiveDialogOpen] = useState(false)
  // #11: a People Leader viewing a team member can give feedback themselves,
  // not only request it (never about themselves).
  const canGiveFeedback =
    userRole === 'manager' && !!userId && !!user?.employeeId && userId !== user.employeeId
  const [requestDialogType, setRequestDialogType] = useState<'MATRIX' | 'UPWARD_360' | 'EMPLOYEE_REQUESTED' | 'INFORMATION_CHECKIN'>('MATRIX')
  const [selectedRequestId, setSelectedRequestId] = useState<string | null>(null)
  const [userDisplayNames, setUserDisplayNames] = useState<Record<string, string>>({})
  const [copiedLinks, setCopiedLinks] = useState<Record<string, boolean>>({})
  const [holisticAnalysis, setHolisticAnalysis] = useState<any>(null)
  const [loadingHolistic, setLoadingHolistic] = useState(false)
  
  // Use refs to track fetching state and cached IDs without causing re-renders
  const fetchingNamesRef = useRef(false)
  const cachedUserIdsRef = useRef<Set<string>>(new Set())
  const hasOpenedRequestRef = useRef(false)
  
  // Handle requestId URL parameter - open the request automatically
  useEffect(() => {
    const requestId = searchParams.get('requestId')
    const campaignId = searchParams.get('campaign')

    if (requestId && !hasOpenedRequestRef.current && pendingStakeholderResponses.length > 0) {
      // Check if this request is in pending responses
      const isPending = pendingStakeholderResponses.some(
        resp => resp.feedback_request_id === requestId
      )

      if (isPending) {
        if (import.meta.env.DEV) console.log('📧 Opening feedback response dialog from email link:', requestId)
        // Switch to pending tab
        setActiveTab('pending-responses')
        // Open the RESPONSE dialog (not detail view)
        setSelectedRequestId(requestId)
        setResponseDialogOpen(true)
        // Remove requestId from URL
        searchParams.delete('requestId')
        setSearchParams(searchParams)
        hasOpenedRequestRef.current = true
      }
    } else if (campaignId && !hasOpenedRequestRef.current && pendingStakeholderResponses.length > 0) {
      // Campaign-invite email link (no per-request id): find the user's
      // pending stakeholder row for this campaign and open it directly.
      const campaignPending = pendingStakeholderResponses.find(
        (resp) => resp.feedback_request?.campaign_id === campaignId,
      )
      if (campaignPending) {
        if (import.meta.env.DEV)
          console.log('📧 Opening campaign feedback from email link:', campaignId)
        setActiveTab('pending-responses')
        setSelectedRequestId(campaignPending.feedback_request_id)
        setResponseDialogOpen(true)
        // Don't strip `campaign=` from the URL — the page itself uses it
        // for the campaign-status tile context.
        hasOpenedRequestRef.current = true
      } else {
        // No pending row for this campaign (e.g. already submitted) — still
        // land on the Pending sub-tab rather than My Requests so the user
        // sees the inbox they were directed to.
        setActiveTab('pending-responses')
      }
    }
  }, [searchParams, setSearchParams, pendingStakeholderResponses])

  const handleRespond = (requestId: string) => {
    setSelectedRequestId(requestId)
    setResponseDialogOpen(true)
  }

  const handleViewDetails = (requestId: string) => {
    setSelectedRequestId(requestId)
    setDetailViewOpen(true)
  }

  const loadHolisticAnalysis = async () => {
    if (!userId) return
    
    setLoadingHolistic(true)
    try {
      if (import.meta.env.DEV) console.log('📊 Loading AI Summary for:', userId)
      // Employees: only their own requested feedback (exclude manager-initiated).
      // Managers (People Leader view): only manager-initiated feedback — a team
      // member's private self-requested feedback must never feed the PL's AI
      // Summary (release-gating, May 2026 rework).
      const analysis = await feedbackService.getHolisticFeedbackAnalysis(
        userId,
        userRole === 'employee' ? { employee_only: true } : { manager_only: true }
      )
      if (import.meta.env.DEV) console.log('📊 AI Summary loaded:', analysis)
      setHolisticAnalysis(analysis)
    } catch (error: any) {
      console.error('Failed to load AI Summary:', error)
      if (error.response?.status === 404) {
        toast.error('No completed feedback found for analysis')
      } else {
        toast.error('Failed to load AI Summary')
      }
      setHolisticAnalysis(null)
    } finally {
      setLoadingHolistic(false)
    }
  }

  const handleCopyLink = async (stakeholder: FeedbackStakeholder, requestId: string) => {
    if (!stakeholder.anonymity_token) {
      toast.error('No access token available for this stakeholder')
      return
    }

    const baseUrl = window.location.origin
    const link = `${baseUrl}/feedback/respond/${stakeholder.anonymity_token}`
    
    try {
      await navigator.clipboard.writeText(link)
      setCopiedLinks(prev => ({ ...prev, [stakeholder.id!]: true }))
      toast.success(`Link copied for ${stakeholder.stakeholder_name}`)
      
      // Reset copied state after 2 seconds
      setTimeout(() => {
        setCopiedLinks(prev => ({ ...prev, [stakeholder.id!]: false }))
      }, 2000)
    } catch (err) {
      toast.error('Failed to copy link')
      console.error('Copy failed:', err)
    }
  }

  // Fetch display names for user IDs - with deduplication and caching
  useEffect(() => {
    const fetchUserNames = async () => {
      // Prevent concurrent fetches
      if (fetchingNamesRef.current) {
        if (import.meta.env.DEV) console.log('⏳ Already fetching names, skipping...')
        return
      }

      const userIds = new Set<string>()
      
      // Helper to check if an ID is a valid employee ID
      const isValidEmployeeId = (id: string) => {
        if (!id) return false
        // Valid IDs are typically numeric personnel numbers
        return /^\d+$/.test(id) || id.startsWith('TEST')
      }
      
      // Add the current user (team member being viewed)
      if (isValidEmployeeId(userId)) userIds.add(userId)
      
      // Collect all user IDs from requests and responses (validate each)
      requests.forEach(r => {
        if (isValidEmployeeId(r.requested_for_user_id)) userIds.add(r.requested_for_user_id)
        if (isValidEmployeeId(r.requested_by_user_id)) userIds.add(r.requested_by_user_id)
      })
      pendingStakeholderResponses.forEach(s => {
        const id = s.feedback_request?.requested_for_user_id
        if (id && isValidEmployeeId(id)) userIds.add(id)
      })
      completedStakeholderResponses.forEach(s => {
        const id = s.feedback_request?.requested_for_user_id
        if (id && isValidEmployeeId(id)) userIds.add(id)
      })

      // Filter out IDs we've already fetched
      const newUserIds = Array.from(userIds).filter(id => !cachedUserIdsRef.current.has(id))
      
      if (newUserIds.length === 0) {
        if (import.meta.env.DEV) console.log('✅ All names already cached')
        return
      }

      if (import.meta.env.DEV) console.log('📋 Fetching names for NEW user IDs:', newUserIds)
      fetchingNamesRef.current = true

      // Fetch display names for each NEW user ID
      // Uses identity service first (accurate), falls back to F&O (may have stale sandbox data)
      const names: Record<string, string> = {}
      for (const id of newUserIds) {
        try {
          const result = await integrationService.getEmployeeDisplayName(id)
          names[id] = result.full_name
          cachedUserIdsRef.current.add(id)
          if (import.meta.env.DEV) console.log(`✅ ${id} -> ${result.full_name} (source: ${result.source})`)
        } catch (error) {
          names[id] = id // Fallback to ID on error
          cachedUserIdsRef.current.add(id)
          console.error(`❌ Failed to fetch name for ${id}:`, error)
        }
      }
      
      if (import.meta.env.DEV) console.log('📝 New names fetched:', names)
      setUserDisplayNames(prev => ({ ...prev, ...names }))
      fetchingNamesRef.current = false
    }

    // Only fetch if we have data or userId
    if (userId || requests.length > 0 || pendingStakeholderResponses.length > 0 || completedStakeholderResponses.length > 0) {
      fetchUserNames()
    }
  }, [userId, requests, pendingStakeholderResponses, completedStakeholderResponses])

  const getDisplayName = (userId: string) => {
    if (!userId) return 'Unknown'
    return userDisplayNames[userId] || userId
  }

  const getRequestTypeColor = (type: string) => {
    // Simple (default) view: one calm heritage-blue tone for every request type
    // — the label already differentiates them. Detailed view keeps the
    // per-type colour coding for power users.
    if (!isDetailed) return 'bg-heritage-blue/10 text-heritage-blue border-heritage-blue/20'
    switch (type) {
      case 'matrix': return 'bg-blue-100 text-blue-800 border-blue-200'
      case 'upward_360': return 'bg-purple-100 text-purple-800 border-purple-200'
      default: return 'bg-gray-100 text-gray-800 border-gray-200'
    }
  }

  // #32: render statuses / relationship codes in consistent Title Case so the
  // chips don't mix "My Requests" (title) with "completed" (lowercase) or
  // raw "DIRECT_REPORT" codes.
  const toTitleCase = (value?: string) =>
    (value || '')
      .replace(/[_-]+/g, ' ')
      .toLowerCase()
      .replace(/\b\w/g, (c) => c.toUpperCase())

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'matrix': return 'Matrix'
      case 'upward_360': return 'Upward 360°'
      case 'employee_requested': return userRole === 'manager' ? 'Team Member-Initiated' : 'My Requests'
      case 'information_checkin': return 'Info Check-in'
      case 'project': return 'UAT / Project'
      default: return type
    }
  }

  const managerInitiatedRequests = requests.filter(r => r.request_type === 'matrix' || r.request_type === 'upward_360')
  const employeeRequestedRequests = requests.filter(r => r.request_type === 'employee_requested' || r.request_type === 'project')

  // Debug: Log feedback data for manager
  useEffect(() => {
    if (userRole === 'manager') {
      if (import.meta.env.DEV) console.log('🎯 StakeholderFeedbackSystem - Manager View Debug:')
      console.log('  Total requests:', requests.length)
      if (import.meta.env.DEV) console.log('  Manager-initiated:', managerInitiatedRequests.length)
      console.log('  Team member-requested:', employeeRequestedRequests.length)
      if (import.meta.env.DEV) console.log('  Requests data:', requests)
    }
  }, [requests, userRole])

  return (
    <div className="space-y-6">
      {(userRole === 'manager') && (
        <div className="flex items-center justify-end">
          <div className="flex gap-2">
            {canGiveFeedback && (
              <Button variant="outline" onClick={() => setGiveDialogOpen(true)}>
                <MessageSquarePlus className="h-4 w-4 mr-2" />
                Give Feedback
              </Button>
            )}
            <Button
              onClick={() => {
                setRequestDialogType('MATRIX')
                setRequestDialogOpen(true)
              }}
              className="bg-heritage-blue hover:bg-heritage-blue-dark"
            >
              <Send className="h-4 w-4 mr-2" />
              Request Feedback
            </Button>
          </div>
        </div>
      )}

      {loading && (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-heritage-blue mr-3"></div>
          <span className="text-gray-600">Loading feedback data...</span>
        </div>
      )}

      {pendingStakeholderResponses.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3"
        >
          <Clock className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <div>
              <h3 className="font-semibold text-blue-900">Feedback Requests Pending</h3>
              <p className="text-sm text-blue-800 mt-1">
                You have {pendingStakeholderResponses.length} feedback request(s) awaiting your response.
              </p>
            </div>
          </div>
        </motion.div>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full min-w-0 max-w-full">
        {/* Wrapping tab strip: every tab stays fully visible at every width
            (375 / 768 / 1024 / 1440) by wrapping onto multiple lines instead of
            scroll-cutting. Tabs are content-sized (no flex-1) and the strip is
            auto-height with a calm pill active state, so a wrapped second row
            still reads as intentional, not ragged. */}
        <TabsList className="flex h-auto w-full min-w-0 max-w-full flex-wrap items-stretch justify-start gap-1 rounded-none border-b border-gray-200 bg-white p-1.5 text-xs sm:text-sm">
          {/* People Leader-Initiated tab - only visible to managers */}
          {userRole === 'manager' && (
            <TabsTrigger value="manager-initiated" className="flex-1 min-w-[160px] justify-center rounded-lg px-3 py-2 text-gray-600 data-[state=active]:bg-heritage-blue data-[state=active]:text-white data-[state=active]:shadow-none">
              <Users className="h-4 w-4 mr-2" />
              People Leader-Initiated
              {managerInitiatedRequests.length > 0 && (
                <Badge variant="secondary" className="ml-2">{managerInitiatedRequests.length}</Badge>
              )}
            </TabsTrigger>
          )}
          {/* "Team Member-Initiated" feedback is the employee's OWN private
              development tool. Per the May 2026 release-gating decision, a
              People Leader never sees it raw here — team feedback reaches the
              PL only through released Impact Portfolio entries. So this tab is
              the team member's "My Requests" surface only. */}
          {userRole === 'employee' && (
            <TabsTrigger value="employee-requested" className="flex-1 min-w-[160px] justify-center rounded-lg px-3 py-2 text-gray-600 data-[state=active]:bg-heritage-blue data-[state=active]:text-white data-[state=active]:shadow-none">
              <ThumbsUp className="h-4 w-4 mr-2" />
              My Requests
              {employeeRequestedRequests.length > 0 && (
                <Badge variant="secondary" className="ml-2">{employeeRequestedRequests.length}</Badge>
              )}
            </TabsTrigger>
          )}
          {userRole === 'employee' && (
            <>
              <TabsTrigger value="pending-responses" className="flex-1 min-w-[160px] justify-center rounded-lg px-3 py-2 text-gray-600 data-[state=active]:bg-heritage-blue data-[state=active]:text-white data-[state=active]:shadow-none">
                <Clock className="h-4 w-4 mr-2" />
                Pending
                {pendingStakeholderResponses.length > 0 && (
                  <Badge variant="secondary" className="ml-2">{pendingStakeholderResponses.length}</Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="completed-responses" className="flex-1 min-w-[160px] justify-center rounded-lg px-3 py-2 text-gray-600 data-[state=active]:bg-heritage-blue data-[state=active]:text-white data-[state=active]:shadow-none">
                <CheckCircle className="h-4 w-4 mr-2" />
                Completed
                {completedStakeholderResponses.length > 0 && (
                  <Badge variant="secondary" className="ml-2">{completedStakeholderResponses.length}</Badge>
                )}
              </TabsTrigger>
            </>
          )}
          <TabsTrigger value="holistic-insights" onClick={loadHolisticAnalysis} className="flex-1 min-w-[160px] justify-center rounded-lg px-3 py-2 text-gray-600 data-[state=active]:bg-heritage-blue data-[state=active]:text-white data-[state=active]:shadow-none">
            <Brain className="h-4 w-4 mr-2" />
            AI Summary
          </TabsTrigger>
        </TabsList>

        <TabsContent value="manager-initiated" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Matrix & Upward Feedback Requests
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {managerInitiatedRequests.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <MessageSquare className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <p>No people leader-initiated feedback requests yet</p>
                </div>
              ) : (
                managerInitiatedRequests.map((request) => (
                  <motion.div
                    key={request.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="border rounded-lg p-4"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge className={getRequestTypeColor(request.request_type)}>
                            {getTypeLabel(request.request_type)}
                          </Badge>
                          {request.is_confidential && (
                            <Badge variant="outline" className="text-xs">
                              <Shield className="h-3 w-3 mr-1" />
                              Confidential
                            </Badge>
                          )}
                          {request.ai_analysis_enabled && (
                            <Badge variant="outline" className="text-xs">
                              <Brain className="h-3 w-3 mr-1" />
                              AI Analysis
                            </Badge>
                          )}
                        </div>

                        {request.title && (
                          <p className="text-sm font-semibold text-gray-900">
                            {request.title}
                          </p>
                        )}
                        <p className="text-xs text-gray-600">
                          Feedback for: {getDisplayName(request.requested_for_user_id)}
                        </p>
                        
                        <div className="flex items-center gap-4 mt-2 text-sm text-gray-600">
                          <span>{request.total_stakeholders ?? 0} stakeholders</span>
                          <span>•</span>
                          <span>
                            {request.responses_received ?? 0} responded
                          </span>
                        </div>

                        {/* External Stakeholder Links */}
                        {request.stakeholders && request.stakeholders.filter(s => s.category === 'external' && s.anonymity_token).length > 0 && (
                          <div className="mt-3 pt-3 border-t space-y-2">
                            <p className="text-xs font-medium text-gray-700 flex items-center gap-1">
                              <ExternalLink className="h-3 w-3" />
                              External Stakeholder Links
                            </p>
                            <div className="space-y-1">
                              {request.stakeholders
                                .filter(s => s.category === 'external' && s.anonymity_token)
                                .map(stakeholder => (
                                  <div key={stakeholder.id} className="flex items-center justify-between gap-2 text-xs">
                                    <span className="text-gray-600 truncate flex-1">
                                      {stakeholder.stakeholder_name} ({stakeholder.stakeholder_email})
                                    </span>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => handleCopyLink(stakeholder, request.id!)}
                                      className="h-6 px-2"
                                    >
                                      {copiedLinks[stakeholder.id!] ? (
                                        <>
                                          <Check className="h-3 w-3 mr-1 text-green-600" />
                                          <span className="text-green-600">Copied</span>
                                        </>
                                      ) : (
                                        <>
                                          <Copy className="h-3 w-3 mr-1" />
                                          Copy Link
                                        </>
                                      )}
                                    </Button>
                                  </div>
                                ))
                              }
                            </div>
                          </div>
                        )}
                      </div>

                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => handleViewDetails(request.id!)}
                      >
                        <Eye className="h-4 w-4 mr-2" />
                        View
                      </Button>
                    </div>
                  </motion.div>
                ))
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="employee-requested">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
              <CardTitle className="sr-only">{userRole === 'manager' ? 'Team Member-Initiated' : 'My Requests'}</CardTitle>
              {userRole === 'employee' && (
                <div className="flex gap-2 ml-auto">
                  {/* #32: primary brand colour regardless of empty/non-empty state */}
                  <Button
                    onClick={() => onCreateRequest({ type: 'employee-requested' })}
                    variant="default"
                    className="bg-heritage-blue hover:bg-heritage-blue-dark"
                  >
                    <Send className="h-4 w-4 mr-2" />
                    Request Feedback
                  </Button>
                </div>
              )}
            </CardHeader>
            <CardContent className="space-y-4">
              {/* #31: feedback no longer auto-integrates — the team member decides. */}
              <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
                <p className="text-sm leading-relaxed text-blue-900">
                  Request feedback from stakeholders. You decide which responses to add to
                  your portfolio after reviewing them.
                </p>
              </div>
              
              {employeeRequestedRequests.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <p>No feedback requests yet</p>
                </div>
              ) : (
                employeeRequestedRequests.map((request) => (
                  <motion.div
                    key={request.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="border rounded-lg p-4"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge className={getRequestTypeColor(request.request_type)}>
                            {getTypeLabel(request.request_type)}
                          </Badge>
                          <Badge variant="outline" className="text-xs">
                            {toTitleCase(request.status)}
                          </Badge>
                        </div>

                        {request.title && (
                          <p className="text-sm font-semibold text-gray-900">
                            {request.title}
                          </p>
                        )}
                        <p className="text-xs text-gray-600">
                          Feedback for: {getDisplayName(request.requested_for_user_id)}
                        </p>
                        
                        <div className="flex items-center gap-4 mt-2 text-sm text-gray-600">
                          <span>{request.total_stakeholders ?? 0} stakeholders</span>
                          <span>•</span>
                          <span>
                            {request.responses_received ?? 0} responded
                          </span>
                        </div>

                        {/* External Stakeholder Links */}
                        {request.stakeholders && request.stakeholders.filter(s => s.category === 'external' && s.anonymity_token).length > 0 && (
                          <div className="mt-3 pt-3 border-t space-y-2">
                            <p className="text-xs font-medium text-gray-700 flex items-center gap-1">
                              <ExternalLink className="h-3 w-3" />
                              External Stakeholder Links
                            </p>
                            <div className="space-y-1">
                              {request.stakeholders
                                .filter(s => s.category === 'external' && s.anonymity_token)
                                .map(stakeholder => (
                                  <div key={stakeholder.id} className="flex items-center justify-between gap-2 text-xs">
                                    <span className="text-gray-600 truncate flex-1">
                                      {stakeholder.stakeholder_name} ({stakeholder.stakeholder_email})
                                    </span>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => handleCopyLink(stakeholder, request.id!)}
                                      className="h-6 px-2"
                                    >
                                      {copiedLinks[stakeholder.id!] ? (
                                        <>
                                          <Check className="h-3 w-3 mr-1 text-green-600" />
                                          <span className="text-green-600">Copied</span>
                                        </>
                                      ) : (
                                        <>
                                          <Copy className="h-3 w-3 mr-1" />
                                          Copy Link
                                        </>
                                      )}
                                    </Button>
                                  </div>
                                ))
                              }
                            </div>
                          </div>
                        )}
                      </div>

                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => handleViewDetails(request.id!)}
                      >
                        <Eye className="h-4 w-4 mr-2" />
                        View
                      </Button>
                    </div>
                  </motion.div>
                ))
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="pending-responses">
          <Card>
            <CardHeader>
              <CardTitle>Pending Responses</CardTitle>
            </CardHeader>
            <CardContent>
              {pendingStakeholderResponses.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <CheckCircle className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <p>No pending feedback requests</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {pendingStakeholderResponses.map((stakeholder) => {
                    const request = stakeholder.feedback_request
                    const requestType = request?.request_type || 'feedback'
                    const requestedForId = request?.requested_for_user_id || 'Unknown'
                    const requestedForName = getDisplayName(requestedForId)
                    const dueDate = feedbackDueDateLabel(request?.due_date) ?? 'No due date'
                    // Past only once the recommended South African day has ended
                    const isOverdue = isPastRecommendedDate(request?.due_date)
                    
                    // #28: prefer the enriched requester name/title from the
                    // backend over the raw personnel code.
                    const requesterName =
                      stakeholder.requester?.display_name
                      || getDisplayName(request?.requested_by_user_id || '')
                    const requesterTitle = stakeholder.requester?.job_title

                    return (
                      <div key={stakeholder.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 space-y-2">
                            <div className="flex items-center gap-2 flex-wrap">
                              {request?.campaign_id ? (
                                <Badge className="bg-purple-100 text-purple-700 hover:bg-purple-100">
                                  Annual Campaign
                                </Badge>
                              ) : (
                                /* #29: show the inferred relationship to the
                                   requester (Direct Report / Peer / External …)
                                   instead of the stakeholder category. */
                                <Badge variant="outline" className="border-slate-200 text-slate-600">
                                  {toTitleCase(stakeholder.relationship || stakeholder.category)}
                                </Badge>
                              )}
                              {request?.is_confidential && (
                                <Badge variant="secondary" className="bg-purple-50 text-purple-700">
                                  <Shield className="h-3 w-3 mr-1" />
                                  Confidential
                                </Badge>
                              )}
                              {isOverdue && (
                                /* #24: recommended date is a gentle nudge, not a
                                   hard deadline — keep this quiet and neutral,
                                   never a RAG-red "Overdue" alarm. */
                                <Badge variant="outline" className="border-stone-200 bg-stone-50 text-stone-500 font-normal">
                                  Past recommended date
                                </Badge>
                              )}
                            </div>

                            <div>
                              <p className="text-sm font-semibold text-gray-900">
                                {request?.title || 'Feedback Request'}
                              </p>
                              <p className="text-xs text-gray-600 mt-1">
                                {request?.campaign_id ? (
                                  <span className="italic">
                                    Confidential — your responses are not attributed to you.
                                  </span>
                                ) : (
                                  <>
                                    {/* #28: requested by a named person, not a code */}
                                    Requested by{' '}
                                    <span className="font-medium">{requesterName}</span>
                                    {requesterTitle && (
                                      <span className="text-gray-500"> · {requesterTitle}</span>
                                    )}
                                    {requestedForName && requestedForName !== requesterName && (
                                      <span className="text-gray-500"> · for {requestedForName}</span>
                                    )}
                                  </>
                                )}
                              </p>
                            </div>

                            <div className="flex items-center gap-4 text-xs text-gray-500">
                              <span className="flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                {/* #24: recommended, not hard due date */}
                                Recommended by: {dueDate}
                              </span>
                              {request?.custom_questions && (
                                <span>{request.custom_questions.length} questions</span>
                              )}
                            </div>
                          </div>
                          
                          <Button 
                            onClick={() => handleRespond(stakeholder.feedback_request_id)}
                            className="bg-heritage-blue hover:bg-heritage-blue/90 shrink-0"
                          >
                            <MessageSquare className="h-4 w-4 mr-2" />
                            Respond
                          </Button>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="completed-responses">
          <Card>
            <CardHeader>
              <CardTitle>Completed Responses</CardTitle>
            </CardHeader>
            <CardContent>
              {completedStakeholderResponses.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <CheckCircle className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <p>No completed responses yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {completedStakeholderResponses.map((stakeholder) => {
                    const request = stakeholder.feedback_request
                    const requestedForId = request?.requested_for_user_id || 'Unknown'
                    const requestedForName = getDisplayName(requestedForId)
                    const submittedDate = stakeholder.response_submitted_at 
                      ? new Date(stakeholder.response_submitted_at).toLocaleDateString() 
                      : 'Unknown'
                    
                    // #28: enriched requester identity on completed inbox rows too.
                    const requesterName =
                      stakeholder.requester?.display_name
                      || getDisplayName(request?.requested_by_user_id || '')
                    const requesterTitle = stakeholder.requester?.job_title

                    return (
                      <div key={stakeholder.id} className="border rounded-lg p-4 bg-gray-50">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 space-y-2">
                            <div className="flex items-center gap-2 flex-wrap">
                              {request?.campaign_id ? (
                                <Badge className="bg-purple-100 text-purple-700 hover:bg-purple-100">
                                  Annual Campaign
                                </Badge>
                              ) : (
                                /* #29: relationship chip, not category. */
                                <Badge variant="outline" className="border-slate-200 text-slate-600">
                                  {toTitleCase(stakeholder.relationship || stakeholder.category)}
                                </Badge>
                              )}
                              {request?.is_confidential && (
                                <Badge variant="secondary" className="bg-purple-50 text-purple-700">
                                  <Shield className="h-3 w-3 mr-1" />
                                  Confidential
                                </Badge>
                              )}
                              <Badge variant="secondary" className="bg-green-50 text-green-700">
                                <CheckCircle className="h-3 w-3 mr-1" />
                                Completed
                              </Badge>
                            </div>

                            <div>
                              <p className="text-sm font-semibold text-gray-900">
                                {request?.title || 'Feedback Request'}
                              </p>
                              <p className="text-xs text-gray-600 mt-1">
                                {request?.campaign_id ? (
                                  <span className="italic">
                                    Anonymous to the subject — your name was not shared.
                                  </span>
                                ) : (
                                  <>
                                    {/* #28 */}
                                    Requested by{' '}
                                    <span className="font-medium">{requesterName}</span>
                                    {requesterTitle && (
                                      <span className="text-gray-500"> · {requesterTitle}</span>
                                    )}
                                    {requestedForName && requestedForName !== requesterName && (
                                      <span className="text-gray-500"> · for {requestedForName}</span>
                                    )}
                                  </>
                                )}
                              </p>
                            </div>
                            
                            <div className="flex items-center gap-4 text-xs text-gray-500">
                              <span className="flex items-center gap-1">
                                <CheckCircle className="h-3 w-3" />
                                Submitted: {submittedDate}
                              </span>
                              {request?.custom_questions && (
                                <span>{request.custom_questions.length} questions answered</span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* AI Summary Tab */}
        <TabsContent value="holistic-insights" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Brain className="h-5 w-5 text-purple-600" />
                AI Summary
              </CardTitle>
              <p className="text-sm text-gray-600">
                AI-powered comprehensive analysis aggregating all feedback cycles
              </p>
            </CardHeader>
            <CardContent>
              {loadingHolistic ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mr-3"></div>
                  <span className="text-gray-600">Analysing all feedback...</span>
                </div>
              ) : !holisticAnalysis ? (
                <div className="text-center py-8 text-gray-500">
                  <Brain className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <p>Click the tab to load AI Summary</p>
                </div>
              ) : !holisticAnalysis.analysis_available ? (
                <div className="text-center py-8">
                  <MessageSquare className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <p className="text-gray-600 font-medium">{holisticAnalysis.message || 'No completed feedback available for analysis'}</p>
                  <p className="text-sm text-gray-500 mt-2">
                    Total feedback cycles: {holisticAnalysis.total_feedback_cycles || 0}
                  </p>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Analysis Header */}
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <Brain className="h-5 w-5 text-purple-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <h3 className="font-semibold text-purple-900">Analysis Overview</h3>
                        <p className="text-sm text-purple-800 mt-1">
                          {holisticAnalysis.total_feedback_cycles} feedback cycles analysed • {holisticAnalysis.total_responses} total responses
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Executive Summary */}
                  {holisticAnalysis.analysis?.executive_summary && (
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center gap-2">
                        <Sparkles className="h-5 w-5 text-purple-600" />
                        Executive Summary
                      </h3>
                      <p className="text-gray-700 bg-gray-50 rounded-lg p-4">
                        {holisticAnalysis.analysis.executive_summary}
                      </p>
                    </div>
                  )}

                  {/* Key Strengths */}
                  {holisticAnalysis.analysis?.key_strengths && holisticAnalysis.analysis.key_strengths.length > 0 && (
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                        <ThumbsUp className="h-5 w-5 text-green-600" />
                        Key Strengths
                      </h3>
                      <div className="space-y-3">
                        {holisticAnalysis.analysis.key_strengths.map((strength: any, idx: number) => (
                          <div key={idx} className="border border-green-200 bg-green-50 rounded-lg p-4">
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex-1">
                                <h4 className="font-semibold text-green-900">{strength.theme}</h4>
                                <p className="text-sm text-green-800 mt-1">{strength.description}</p>
                              </div>
                              <Badge className="bg-green-600 text-white shrink-0">
                                {strength.evidence_count} mentions
                              </Badge>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Development Areas */}
                  {holisticAnalysis.analysis?.development_areas && holisticAnalysis.analysis.development_areas.length > 0 && (
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                        <TrendingUp className="h-5 w-5 text-blue-600" />
                        Development Areas
                      </h3>
                      <div className="space-y-3">
                        {holisticAnalysis.analysis.development_areas.map((area: any, idx: number) => (
                          <div key={idx} className="border border-blue-200 bg-blue-50 rounded-lg p-4">
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex-1">
                                <h4 className="font-semibold text-blue-900">{area.theme}</h4>
                                <p className="text-sm text-blue-800 mt-1">{area.description}</p>
                              </div>
                              <Badge className="bg-blue-600 text-white shrink-0">
                                {area.evidence_count} mentions
                              </Badge>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Trends */}
                  {holisticAnalysis.analysis?.trends && holisticAnalysis.analysis.trends.length > 0 && (
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                        <BarChart3 className="h-5 w-5 text-amber-600" />
                        Trends & Patterns
                      </h3>
                      <div className="space-y-3">
                        {holisticAnalysis.analysis.trends.map((trend: any, idx: number) => (
                          <div key={idx} className="border rounded-lg p-4">
                            <h4 className="font-semibold text-gray-900">{trend.pattern}</h4>
                            <p className="text-sm text-gray-700 mt-1">{trend.description}</p>
                            <p className="text-sm text-purple-700 mt-2 font-medium">
                              💡 {trend.recommendation}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Actionable Recommendations */}
                  {holisticAnalysis.analysis?.actionable_recommendations && holisticAnalysis.analysis.actionable_recommendations.length > 0 && (
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                        <Lightbulb className="h-5 w-5 text-amber-500" />
                        Actionable Recommendations
                      </h3>
                      <div className="space-y-3">
                        {holisticAnalysis.analysis.actionable_recommendations.map((rec: any, idx: number) => (
                          <div key={idx} className="border rounded-lg p-4">
                            <div className="flex items-start gap-3">
                              <Badge className={
                                !isDetailed ? 'bg-heritage-blue/10 text-heritage-blue border border-heritage-blue/20' :
                                rec.priority === 'high' ? 'bg-red-600' :
                                rec.priority === 'medium' ? 'bg-amber-600' :
                                'bg-gray-600'
                              }>
                                {rec.priority}
                              </Badge>
                              <div className="flex-1">
                                <h4 className="font-semibold text-gray-900">{rec.action}</h4>
                                <p className="text-sm text-gray-700 mt-1">
                                  <span className="font-medium">Impact:</span> {rec.expected_impact}
                                </p>
                                <p className="text-xs text-gray-500 mt-1">
                                  Timeframe: {rec.timeframe}
                                </p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Overall Ratings */}
                  {holisticAnalysis.analysis?.overall_rating && (
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                        <Star className="h-5 w-5 text-amber-500" />
                        Overall Impact & Growth Indicators
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {Object.entries(holisticAnalysis.analysis.overall_rating).map(([key, value]: [string, any]) => (
                          <div key={key} className="border rounded-lg p-4 text-center">
                            <p className="text-sm text-gray-600 capitalize">{key.replace('_', ' ')}</p>
                            <p className="text-2xl font-bold text-purple-600 mt-1">{value}/5</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Response Dialog */}
      {selectedRequestId && (
        <FeedbackResponseDialog
          open={responseDialogOpen}
          onOpenChange={setResponseDialogOpen}
          requestId={selectedRequestId}
          onSuccess={() => {
            onRefresh?.()
          }}
        />
      )}

      {/* Detail View Dialog */}
      {detailViewOpen && selectedRequestId && (
        <FeedbackDetailView
          open={detailViewOpen}
          onOpenChange={setDetailViewOpen}
          requestId={selectedRequestId}
          onRefresh={onRefresh}
        />
      )}

      {/* Give Feedback - a People Leader's own feedback on the team member.
          Stays mounted so answers that failed to save can be sent again
          without recording a second request. */}
      {canGiveFeedback && (
        <GiveFeedbackDialog
          open={giveDialogOpen}
          onOpenChange={setGiveDialogOpen}
          subjectId={userId}
          subjectName={getDisplayName(userId)}
          onGiven={() => {
            setActiveTab('manager-initiated')
            onRefresh?.()
          }}
        />
      )}

      {/* Feedback Request Dialog - For managers to create feedback requests */}
      {requestDialogOpen && (
        <FeedbackRequestDialog
          open={requestDialogOpen}
          onClose={() => setRequestDialogOpen(false)}
          requestType={requestDialogType}
          currentUserId={userId}
          onSubmit={async (data) => {
            if (import.meta.env.DEV) console.log('🎯 StakeholderFeedbackSystem - onSubmit called with:', data)
            try {
              if (import.meta.env.DEV) console.log('📡 Calling onCreateRequest...')
              const result = await onCreateRequest(data)
              if (import.meta.env.DEV) console.log('✅ onCreateRequest result:', result)
              setRequestDialogOpen(false)
              if (onRefresh) {
                onRefresh()
              }
              return result
            } catch (error) {
              console.error('❌ onCreateRequest error:', error)
              // Error already handled in parent
              throw error
            }
          }}
        />
      )}
    </div>
  )
}
